import { freezeMap } from "./helpers.js";

const COMMON_PRODUCTION_METRICS = Object.freeze([
  { value: "tonnes", text: "Tonnes" },
  { value: "heads", text: "Heads" },
  { value: "kg_per_head", text: "Kg/Head" },
]);

const getNoLabsDescription = (productName) => `No living labs currently working with ${productName}`;

const productsList = [
  {
    value: "beef",
    text: "Beef",
    collections: [
      { value: "carcass_prices", text: "Carcass" },
      { value: "live_animal_prices", text: "Live Animal" },
    ],
    carcass_prices: {
      products: ["Adult male indicative price", "Bulls",
        "Calves slaughtered <8M", "Cows", "Heifers", "Steers", "Young bulls", "Young cattle"],
    },
    live_animal_prices: {
      products: ["Male Calves Beef Type", "Male Calves Dairy Type", "Yearling Female Store Cattle",
        "Yearling Male Store Cattle", "Young Store Cattle"],
    },
    production: {
      products: ["Bull", "Bullock", "Calf", "Cow", "Heifer", "Young cattle"],
      productionMetrics: COMMON_PRODUCTION_METRICS,
    },
    description: getNoLabsDescription("beef"),
    image: "/product_images/beef.png",
  },
  {
    value: "cereals",
    text: "Cereals",
    collections: ["prices", "production"],
    prices: {
      products: ["Durum wheat", "Feed barley", "Feed maize", "Feed oats", "Feed rye", "Feed wheat",
        "Malting barley", "Milling oats", "Milling rye", "Milling wheat", "Triticale", "Wheat bran"],
    },
    production: {
      products: ["Barley", "Durum wheat", "Maize", "Oat", "Other cereals", "Rye", "Soft wheat", "Sorghum", "Triticale"],
      productionMetrics: [
        { value: "gross_production", text: "Gross Production" },
        { value: "yield", text: "Yield" },
      ],
    },
    mapSet: new Set(["barley", "wheat", "maize", "oats", "rye", "sorghum", "triticale"]),
    image: "/product_images/cereals.png",
  },
  {
    value: "eggs_poultry",
    text: "Eggs",
    collections: ["egg_prices"],
    prices: {
      products: ["Barn", "Cage", "Free range", "Organic"],
    },
    description: "CONCATLL, Probio",
    relevantLLs: ["CONCATLL", "Probio"],
    image: "/product_images/eggs.jpg",
  },
  {
    value: "eggs_poultry",
    text: "Poultry",
    collections: ["poultry_prices", "poultry_production"],
    prices: {
      products: ["Selling price", "Non-retail buying prices", "Retail buying prices"],
    },
    production: {
      products: ["Boiling hen", "Broiler", "Chicken", "Duck", "Goose", "Guinea fowl", "Other poultry", "Poultry meat", "Turkey"],
      productionMetrics: COMMON_PRODUCTION_METRICS,
    },
    description: "CONCATLL, Probio",
    relevantLLs: ["CONCATLL", "Probio"],
    image: "/product_images/poultry.jpg",
  },
  {
    value: "fruit_vegetables",
    text: "Fruits & Vegetables",
    subheader: true,
    collections: ["prices"],
    prices: {
      products: ["abricots", "apples", "asparagus", "avocados", "beans", "cabbages", "carrots", "cauliflowers", "cherries", "clementines", "courgettes", "cucumbers", "egg plants, aubergines", "garlic", "kiwis", "leeks", "lemons", "lettuces", "mandarins", "melons", "mushrooms, cultivated", "nectarines", "onions", "oranges", "peaches", "pears", "peppers", "plums", "satsumas", "strawberries", "table grapes", "tomatoes", "water melons"],
    },
    image: "/product_images/fruit_vegetables.jpg",
  },
  {
    value: "pigmeat",
    text: "Pigmeat",
    collections: [{ value: "carcass_prices", text: "Carcass" }, { value: "cuts_prices", text: "Cuts" }],
    carcass_prices: [],
    cuts_prices: {
      products: ["Belly", "Ham", "Loin", "Minced Meat", "Shoulder"],
      productTypes: ["Selling price", "Non-retail buying price", "Retail buying price"],
    },
    production: {
      productionMetrics: COMMON_PRODUCTION_METRICS,
    },
    description: getNoLabsDescription("pigmeat"),
    image: "/product_images/pigmeat.png",
  },
  {
    value: "milk_dairy",
    text: "Dairy",
    subheader: true,
    collections: ["dairy_prices", "dairy_production"],
    prices: {
      products: ["butter", "butteroil", "cheddar", "cream", "edam", "emmental", "gouda", "smp", "wheypowder", "wmp"],
    },
    description: "Dairy products derived from milk processing",
    image: "/product_images/dairy.jpg",
  },
  {
    value: "milk_dairy",
    text: "Milk",
    collections: ["raw_milk_prices"],
    prices: {
      products: ["Organic raw milk", "Raw milk"],
    },
    production: {
      products: ["Drinking milk", "Skimmed milk powder"],
    },
    description: "EcoReadyMasuria (milk)",
    image: "/product_images/milk.jpg",
  },
  {
    value: "oilseeds_protein_crops",
    text: "Oilseeds",
    collections: ["oilseeds_prices", "oilseeds_production"],
    prices: {
      products: ["Crude rape oil", "Crude soya bean oil", "Crude sunflower oil", "Rapeseed", "Rapeseed meal", "Soya beans", "Soya meal", "Sunflower seed", "Sunflower seed meal"],
      productTypes: ["30-35% protein content", "40-50% protein content", "Above 35% protein content", "Below 30% protein content", "Below 40% protein content", "From dehulled seeds", "High-oleic", "N.A.", "Standard"],
    },
    production: {
      products: ["Broad/field beans", "Field peas", "Linseed", "Lupins", "Rapeseed", "Soybean", "Sunflower seed"],
    },
    description: "EcoReadyMasuria (sunflower), Esappin (rapeseed)",
    image: "/product_images/oilseeds.jpg",
  },
  {
    value: "oilseeds_protein_crops",
    text: "Protein Crops",
    subheader: true,
    collections: ["protein_crops_prices"],
    prices: {
      products: ["Alfalfa", "Broad beans", "Chickpeas", "Lentils", "Lupins", "Peas"],
      productTypes: ["Bales", "Feed", "Food", "Not Defined", "Pellets"],
    },
    description: "Protein rich crops for sustainable agriculture",
    image: "/product_images/protein_crops.jpg",
  },
  {
    value: "olive_oil",
    text: "Olive Oil",
    collections: ["prices", "annual_production"],
    prices: {
      products: ["Crude olive-pomace", "Crude olive-pomace oil (from 5 to 10%)", "Extra virgin",
        "Extra virgin olive oil (up to 0,8°)", "Lampante", "Lampante olive oil (2%)", "Olive-pomace oil (up to 1°)",
        "Refined", "Refined olive oil (up to 0,3°)", "Refined olive-pomace", "Refined olive-pomace oil (up to 0.3%)",
        "Virgin", "Virgin olive oil (up to 2%)"],
    },
    description: "AIDEMEC, CONCATLL, Probio, THALLA",
    image: "/product_images/olive_oil.jpg",
  },
  {
    value: "rice",
    text: "Rice",
    collections: ["prices", "production"],
    prices: {
      products: ["Indica", "Japonica", "N.A.", "Not informed"],
      productTypes: ["Andalucia", "Arbiorio Volano", "Arborio", "Arborio - Volano", "Arborio -Volano", "Ariete", "Augusto", "Avarage", "Average", "Avg", "B", "Baldo", "Baldo E Cammeo", "Balilla", "Balilla - Centauro", "Balilla Centauro", "Balilla E Centauro", "Brisures Camargue", "Caravaggio", "Carnaroli", "Carolina", "Centauro", "Cl 388", "Corpetto", "Crono", "Gleva", "Gleva Valencia", "Gloria", "Grana Verde", "J. Sendra", "L", "Leonardo", "Lido", "Lolla", "Long", "Long A", "Long B", "Longo A", "Longo B", "Loto", "Luna-Ronaldo", "Mezzagrana", "Moyen", "Not informed", "Omega", "Originario", "Originario (Comune)", "Pula", "Pula Max 1% Silicio", "Puntal", "Puntal Andalucia", "Puntal Valencia", "Ribe", "Risetto", "Risetto Parboiled", "Roma", "Roma E Barone", "Rond", "S. Andrea", "Selenio", "Sendra Andalucia", "Sendra Valencia", "Sirio Andalucia", "Sirio Valencia", "Sole", "Sole Cl", "Terra", "Thaibonnet", "Tipo Ribe", "Valencia", "Volano"],
    },
    production: {
      products: ["(1) Round grain paddy rice", "(2) Medium grain paddy rice", "(3) Long grain A paddy rice", "(4) Long grain B paddy rice"],
      productionMetrics: [
        { value: "milled_rice_equivalent_quantity", text: "Milled rice" },
        { value: "rice_husk_quantity", text: "Rice husk" },
      ],
    },
    description: getNoLabsDescription("rice"),
    image: "/product_images/rice.jpg",
  },
  {
    value: "sheep_goat_meat",
    text: "Sheep/Goat Meat",
    collections: ["meat_prices", "production"],
    prices: { products: ["Heavy Lamb", "Light Lamb"] },
    production: {
      products: ["Goat meat", "Sheepmeat"],
      productTypes: ["Slaughterings", "Slaughterings, other than in slaughterhouses"],
      productionMetrics: COMMON_PRODUCTION_METRICS,
    },
    description: getNoLabsDescription("sheep/goat meat"),
    image: "/product_images/sheep_goat_meat.png",
  },
  {
    value: "sugar",
    text: "Sugar",
    collections: ["prices", "production"],
    prices: [],
    production: {
      productionMetrics: [
        { value: "gross_production", text: "Gross Production" },
        { value: "yield", text: "Yield" },
      ],
    },
    description: getNoLabsDescription("sugar"),
    image: "/product_images/sugar.jpg",
  },
  {
    value: "wine",
    text: "Wine",
    collections: ["prices"],
    prices: {
      products: [
        "Albacete vino blanco sin DOP/IGP",
        "Albacete vino tinto sin DOP/IGP",
        "Altri Vini",
        "Badajoz vino blanco sin DOP/IGP",
        "Bari Vino bianco senza DOP/IGP",
        "Bari Vino rosso senza DOP/IGP",
        "Blancs / Vin AOP",
        "Blancs / Vin IGP",
        "Blancs / Vin sans IG avec mention de cépages",
        "Blancs / Vin sans IG sans mention de cépages",
        "Ciudad Real vino blanco sin DOP/IGP",
        "Ciudad Real vino tinto sin DOP/IGP",
        "Lugo Vino bianco senza DOP/IGP",
        "Lugo Vino rosso DOP",
        "Mosel Qba Weiß",
        "Pescara Vino bianco senza DOP/IGP",
        "Pescara Vino rosso senza DOP/IGP",
        "Pfalz QbA Rot Dornfelder",
        "Pfalz Qba Weiß",
        "Pfalz ohne Rebsorteangabe",
        "Ravenna Vino bianco senza DOP/IGP",
        "Rheingau Qba Weiß",
        "Rheinhessen Qba Weiß",
        "Rheinhessen ohne Rebsorteangabe",
        "Rioja DOP vino tinto",
        "Rouges et Rosés / Vin AOP",
        "Rouges et Rosés / Vin IGP",
        "Rouges et Rosés / Vin sans IG avec mention de cépages",
        "Rouges et Rosés / Vin sans IG sans mention de cépages",
        "Rueda DOP vino blanco",
        "Toledo vino blanco sin DOP/IGP",
        "Toledo vino tinto sin DOP/IGP",
        "Trapani Vino bianco senza DOP/IGP",
        "Trapani Vino rosso senza DOP/IGP",
        "Valencia vino tinto sin DOP/IGP",
        "Verona Vino bianco DOP",
        "Verona Vino bianco senza DOP/IGP",
      ],
    },
    description: getNoLabsDescription("wine"),
    image: "/product_images/wine.jpg",
  },
];

productsList.sort((a, b) => a.text.localeCompare(b.text));

export const products = Object.freeze(productsList);

export const ProductsByTextMap = freezeMap(new Map(products.map((p) => [p.text, p])));

export const ProductsMap = freezeMap(new Map(products.map((p) => [p.value, p])));
